import { useState, useRef, RefObject } from 'react'
import { Toast } from 'primereact/toast'
import { confirmDialog } from 'primereact/confirmdialog'
import { 
  analyzeBulkOrders, 
  ExecutionResponse, 
  ExecutionPlan,
  executeBulkAssignment,
  FullFulfillmentOrder,
  PartialFulfillmentOrder,
  FailedOrder
} from '../services/bulkAssignmentService'
import { ShippingOrder } from '../DeShippingTableWrapper'

type DialogStep = 'initial' | 'results' | 'success'

interface UseBulkOrderAssignmentReturn {
  // State
  isProcessing: boolean
  currentStep: DialogStep
  processedResults: {
    full: FullFulfillmentOrder[]
    partial: PartialFulfillmentOrder[]
    failed: FailedOrder[]
  } | null
  executionResults: ExecutionResponse | null
  toast: RefObject<Toast>
  
  // Results selection state
  isSubmitting: boolean
  selectedFull: string[]
  selectedPartial: string[]
  selectedFailed: string[]
  totalSelected: number
  
  // Actions
  handleProcessOrders: (orders: ShippingOrder[]) => Promise<void>
  handleBackToSummary: () => void
  handleExecutionComplete: (response: ExecutionResponse) => void
  resetState: () => void
  
  // Results actions
  handleOrderToggle: (orderId: string, category: 'full' | 'partial' | 'failed') => void
  handleConfirmAssignment: () => void
  handleSubmitAssignment: () => Promise<void>
}

export const useBulkOrderAssignment = (ordersRefresh: () => void): UseBulkOrderAssignmentReturn => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentStep, setCurrentStep] = useState<DialogStep>('initial')
  const [processedResults, setProcessedResults] = useState<any>(null)
  const [executionResults, setExecutionResults] = useState<ExecutionResponse | null>(null)
  const toast = useRef<Toast>(null)

  // Results selection state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedFull, setSelectedFull] = useState<string[]>([])
  const [selectedPartial, setSelectedPartial] = useState<string[]>([])
  const [selectedFailed, setSelectedFailed] = useState<string[]>([])

  const totalSelected = selectedFull.length + selectedPartial.length + selectedFailed.length

  const handleProcessOrders = async (orders: ShippingOrder[]) => {
    setIsProcessing(true)
    setCurrentStep('results') // Move to results step immediately
    
    try {
      // Call the real API
      const orderIds = orders.map(order => order.order_id)
      const response = await analyzeBulkOrders(orderIds)
      
      if (response.success) {
        // Transform API response to match expected format
        const transformedResults = {
          full: response.full_fulfillment,
          partial: response.partial_fulfillment,
          failed: response.failed_orders
        }
        
        setProcessedResults(transformedResults)
        
        // Initialize selections based on results
        setSelectedFull(response.full_fulfillment?.map(order => order.order_id) || [])
        setSelectedPartial(response.partial_fulfillment?.map(order => order.order_id) || [])
        setSelectedFailed([]) // Failed orders start unselected
        
        toast.current?.show({
          severity: 'success',
          summary: 'Analysis Complete',
          detail: `Analyzed ${response.summary.total_orders_analyzed} orders successfully`,
          life: 3000,
        })
      } else {
        throw new Error('API returned unsuccessful response')
      }
    } catch (error) {
      console.error('Error processing orders:', error)
      
      toast.current?.show({
        severity: 'error',
        summary: 'Processing Error',
        detail: error instanceof Error ? error.message : 'Failed to process orders',
        life: 5000,
      })
      
      // Reset to initial view on error
      setCurrentStep('initial')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleBackToSummary = () => {
    setCurrentStep('initial')
    setProcessedResults(null)
  }

  const handleExecutionComplete = (executionResponse: ExecutionResponse) => {
    // Store execution results and show success dialog
    setExecutionResults(executionResponse)
    setCurrentStep('success')
    ordersRefresh()

    toast.current?.show({
      severity: 'success',
      summary: 'Assignment Successful',
      detail: `Successfully processed ${executionResponse.execution_summary.total_orders_processed} orders with ${executionResponse.execution_summary.total_line_items_processed} line items assigned.`,
      life: 3000
    })
  }

  const handleOrderToggle = (orderId: string, category: 'full' | 'partial' | 'failed') => {
    switch (category) {
      case 'full':
        setSelectedFull(prev => 
          prev.includes(orderId) 
            ? prev.filter(id => id !== orderId)
            : [...prev, orderId]
        )
        break
      case 'partial':
        setSelectedPartial(prev => 
          prev.includes(orderId) 
            ? prev.filter(id => id !== orderId)
            : [...prev, orderId]
        )
        break
      case 'failed':
        setSelectedFailed(prev => 
          prev.includes(orderId) 
            ? prev.filter(id => id !== orderId)
            : [...prev, orderId]
        )
        break
    }
  }

  const handleConfirmAssignment = () => {
    const selectedOrdersData = {
      full: selectedFull.length,
      partial: selectedPartial.length,
      failed: selectedFailed.length,
      total: totalSelected
    }

    confirmDialog({
      message: `You are about to assign ${totalSelected} orders: ${selectedOrdersData.full} Full Fulfillment, ${selectedOrdersData.partial} Partial Fulfillment, ${selectedOrdersData.failed} Failed orders. This action cannot be undone.`,
      header: 'Confirm Bulk Assignment',
      icon: 'pi pi-question-circle',
      acceptClassName: 'p-button-primary',
      rejectClassName: 'p-button-outlined',
      accept: async () => {
        await handleSubmitAssignment()
      },
      reject: () => {
        // User cancelled - no action needed
      }
    })
  }

  const handleSubmitAssignment = async () => {
    setIsSubmitting(true)
    
    // Immediately switch to success dialog to show loading states
    setCurrentStep('success')
    
    try {
      // Transform analysis results into execution plan
      const executionPlan: ExecutionPlan = {
        full_fulfillment_orders: [],
        partial_fulfillment_orders: [],
        skipped_orders: []
      }

      // Process selected full fulfillment orders
      if (processedResults?.full) {
        processedResults.full.forEach((order: FullFulfillmentOrder) => {
          if (selectedFull.includes(order.order_id)) {
            executionPlan.full_fulfillment_orders.push({
              order_id: order.order_id,
              store_assignment: {
                store_name: order.recommended_store,
                line_item_ids: order.inventory_details.map(item => item.line_item_id)
              }
            })
          } else {
            executionPlan.skipped_orders.push(order.order_id)
          }
        })
      }

      // Process selected partial fulfillment orders
      if (processedResults?.partial) {
        processedResults.partial.forEach((order: PartialFulfillmentOrder) => {
          if (selectedPartial.includes(order.order_id)) {
            executionPlan.partial_fulfillment_orders.push({
              order_id: order.order_id,
              store_assignments: order.store_assignments.map(assignment => {
                // Extract line_item_ids from the assigned_line_items array
                const lineItemIds = (assignment as any).assigned_line_items?.map((item: any) => item.line_item_id) || []
                
                return {
                  store_name: assignment.store_name,
                  line_item_ids: lineItemIds
                }
              })
            })
          } else {
            executionPlan.skipped_orders.push(order.order_id)
          }
        })
      }

      // Add failed orders to skipped
      if (processedResults?.failed) {
        processedResults.failed.forEach((order: FailedOrder) => {
          if (!selectedFailed.includes(order.order_id)) {
            executionPlan.skipped_orders.push(order.order_id)
          }
        })
      }
      
      // Call the real API
      const executionResponse = await executeBulkAssignment(executionPlan)
      
      if (executionResponse.success) {
        // Set execution results to show actual data
        setExecutionResults(executionResponse)
        ordersRefresh()

        toast.current?.show({
          severity: 'success',
          summary: 'Assignment Successful',
          detail: `Successfully processed ${executionResponse.execution_summary.total_orders_processed} orders with ${executionResponse.execution_summary.total_line_items_processed} line items assigned.`,
          life: 3000
        })
      } else {
        throw new Error('Execution was not successful')
      }
    } catch (error) {
      console.error('Error executing bulk assignment:', error)
      
      toast.current?.show({
        severity: 'error',
        summary: 'Assignment Failed',
        detail: error instanceof Error ? error.message : 'An unexpected error occurred during assignment execution.',
        life: 5000
      })
      
      // Go back to results on error
      setCurrentStep('results')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetState = () => {
    setCurrentStep('initial')
    setProcessedResults(null)
    setIsProcessing(false)
    setExecutionResults(null)
    setSelectedFull([])
    setSelectedPartial([])
    setSelectedFailed([])
    setIsSubmitting(false)
  }

  return {
    // State
    isProcessing,
    currentStep,
    processedResults,
    executionResults,
    toast,
    
    // Results selection state
    isSubmitting,
    selectedFull,
    selectedPartial,
    selectedFailed,
    totalSelected,
    
    // Actions
    handleProcessOrders,
    handleBackToSummary,
    handleExecutionComplete,
    resetState,
    
    // Results actions
    handleOrderToggle,
    handleConfirmAssignment,
    handleSubmitAssignment
  }
} 